import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AppBar,
  Box,
  CircularProgress,
  List,
  ListItem,
  Tab,
  Tabs,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Check as CheckIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { Tables } from "../../../database.types";
import RoundEditor from "./RoundEditor";
import BonusRoundEditor from "./BonusRoundEditor";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { useSupabase } from "../../auth/useSupabase";
import { gameService } from "../../data/gameService";
import {
  isRoundConfigCompleteDB,
  getGameConfigErrors,
} from "../../utils/utils";

interface GameBoardEditorProps {
  gameId: string;
  disabled: boolean;
}

const GameBoardEditor: React.FC<GameBoardEditorProps> = ({
  gameId,
  disabled,
}) => {
  const theme = useTheme();
  const { supabase } = useSupabase();

  const [currentTabIndex, setCurrentTabIndex] = useLocalStorageState<number>(
    "gameboardeditor-current-tab-" + gameId,
    0,
  );

  const [loading, setLoading] = useState(true);
  const [rounds, setRounds] = useState<Tables<"rounds">[]>([]);
  const [questions, setQuestions] = useState<Tables<"questions">[]>([]);
  const [bonusRounds, setBonusRounds] = useState<Tables<"bonus_rounds">[]>([]);
  const [classicAnswers, setClassicAnswers] = useState<
    Tables<"classic_bonus_answers">[]
  >([]);
  const [closestGuessQuestions, setClosestGuessQuestions] = useState<
    Tables<"closest_guess_questions">[]
  >([]);
  const [incompleteErrors, setIncompleteErrors] = useState<string[]>([]);

  const mainBonusRound = bonusRounds.find((br) => br.phase === "bonus") ?? null;
  const overtimeBonusRound =
    bonusRounds.find((br) => br.phase === "overtime") ?? null;

  const fetchData = useCallback(async () => {
    try {
      const [gameRes, roundsRes, questionsRes, bonusRoundsRes] =
        await Promise.all([
          gameService.getById(supabase, gameId),
          gameService.getGameRounds(supabase, gameId),
          gameService.getGameQuestions(supabase, gameId),
          gameService.getGameBonusRounds(supabase, gameId),
        ]);

      setRounds(roundsRes);
      setQuestions(questionsRes);
      setBonusRounds(bonusRoundsRes);

      // Fetch bonus round details for completeness checking
      const mainBonus = bonusRoundsRes.find((br) => br.phase === "bonus");
      let answers: Tables<"classic_bonus_answers">[] = [];
      let cgQuestions: Tables<"closest_guess_questions">[] = [];

      if (mainBonus) {
        [answers, cgQuestions] = await Promise.all([
          gameService.getClassicBonusAnswers(supabase, mainBonus.id),
          gameService.getClosestGuessQuestions(supabase, mainBonus.id),
        ]);
        setClassicAnswers(answers);
        setClosestGuessQuestions(cgQuestions);
      }

      // Compute config errors and sync config_complete
      if (gameRes) {
        const errors = getGameConfigErrors(
          gameRes,
          roundsRes,
          questionsRes,
          mainBonus ?? null,
          answers,
          cgQuestions,
        );
        setIncompleteErrors(errors);

        const shouldBeComplete = errors.length === 0;
        if (gameRes.config_complete !== shouldBeComplete) {
          await gameService.setConfigComplete(
            supabase,
            gameId,
            shouldBeComplete,
          );
        }
      }
    } catch (err) {
      console.error("Error loading game board data:", err);
    } finally {
      setLoading(false);
    }
  }, [supabase, gameId]);

  // Fetch on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Re-fetch on tab change to pick up saves from child editors
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setCurrentTabIndex(newValue);
    fetchData();
  };

  // ── Tab completeness helpers ──────────────────────────────────────────

  const isRoundTabComplete = (roundNumber: number): boolean => {
    const round = rounds.find((r) => r.round_number === roundNumber);
    if (!round) return false;
    const roundQuestions = questions.filter((q) => q.round_id === round.id);
    return isRoundConfigCompleteDB(round, roundQuestions);
  };

  const isBonusTabComplete = (): boolean => {
    if (!mainBonusRound) return false;
    if (mainBonusRound.type === "Classic (List)") {
      const filledAnswers = classicAnswers.filter((a) => a.answer_text);
      return !!mainBonusRound.question_text && filledAnswers.length >= 25;
    } else {
      const filled = closestGuessQuestions.filter(
        (q) => q.question_text && q.correct_answer !== null,
      );
      return filled.length >= 6;
    }
  };

  const refreshTabState = useCallback(async () => {
    try {
      const [newRounds, newBonusRounds] = await Promise.all([
        gameService.getGameRounds(supabase, gameId),
        gameService.getGameBonusRounds(supabase, gameId),
      ]);
      setRounds(newRounds);
      setBonusRounds(newBonusRounds);
    } catch (err) {
      // do nothing
    }
  }, [setRounds, setBonusRounds, gameId, supabase]);

  // ── Loading state ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
        <CircularProgress />
      </Box>
    );
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  const completionIcon = (complete: boolean) =>
    complete ? (
      <CheckIcon fontSize="small" sx={{ color: "success.main" }} />
    ) : (
      <ClearIcon fontSize="small" sx={{ color: "error.main" }} />
    );

  const tabSx = {
    fontSize: { xs: "12px", sm: "14px" },
    fontWeight: 600,
    px: { xs: 1, sm: 2 },
    minHeight: { xs: 42, sm: 48 },
    my: 0.5,
    borderRadius: 1,
  };

  const smallTabSx = {
    ...tabSx,
    fontSize: { xs: "11px", sm: "13px" },
  };

  // ── Tab bar ───────────────────────────────────────────────────────────

  const renderTabs = () => (
    <AppBar
      position="static"
      color="transparent"
      sx={{
        borderRadius: "12px",
        boxShadow: 2,
        overflow: "hidden",
      }}
    >
      <Tabs
        value={currentTabIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        slotProps={{
          indicator: {
            style: {
              height: "100%",
              top: 0,
              borderRadius: 8,
              backgroundColor: theme.palette.primary.main,
              zIndex: 0,
              transition: "all 300ms cubic-bezier(0.4, 0, 0.2, 1)",
            },
          },
        }}
        sx={{
          "& .MuiTabs-scrollButtons": {
            color: "white",
            "&.Mui-disabled": { opacity: 0.3 },
          },
          "& .MuiTab-root": {
            position: "relative",
            zIndex: 1,
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.3),
            },
            "&.Mui-selected": {
              color: "white",
            },
          },
          border: `1px solid ${theme.palette.primary.main}`,
          borderRadius: "12px",
        }}
      >
        {/* Rounds 1–6 (tabs 0–5) */}
        {rounds.slice(0, 6).map((round) => (
          <Tab
            key={round.id}
            data-testid={`round-tab-${round.round_number}`}
            label={`Round ${round.round_number}`}
            icon={completionIcon(isRoundTabComplete(round.round_number))}
            iconPosition="end"
            sx={tabSx}
          />
        ))}

        {/* Bonus round (tab 6) */}
        <Tab
          key="bonus"
          data-testid="bonus-round-tab"
          label="Bonus Round"
          icon={completionIcon(isBonusTabComplete())}
          iconPosition="end"
          sx={smallTabSx}
        />

        {/* Rounds 7–12 (tabs 7–12) */}
        {rounds.slice(6, 12).map((round) => (
          <Tab
            key={round.id}
            data-testid={`round-tab-${round.round_number}`}
            label={
              round.round_number === 10
                ? "Niche Round"
                : `Round ${round.round_number}`
            }
            icon={completionIcon(isRoundTabComplete(round.round_number))}
            iconPosition="end"
            sx={smallTabSx}
          />
        ))}

        {/* Overtime (tab 13) */}
        <Tab
          key="overtime"
          data-testid="overtime-tab"
          label="Overtime"
          icon={completionIcon(overtimeBonusRound !== null)}
          iconPosition="end"
          sx={smallTabSx}
        />
      </Tabs>
    </AppBar>
  );

  // ── Page body ─────────────────────────────────────────────────────────

  const renderPageBody = () => {
    if (currentTabIndex === 13) {
      return (
        <BonusRoundEditor
          key={currentTabIndex}
          bonusRoundId={overtimeBonusRound?.id ?? null}
          gameId={gameId}
          phase="overtime"
          disabled={disabled}
        />
      );
    }

    if (currentTabIndex === 6) {
      return (
        <BonusRoundEditor
          key={currentTabIndex}
          bonusRoundId={mainBonusRound?.id ?? null}
          gameId={gameId}
          phase="bonus"
          disabled={disabled}
        />
      );
    }

    // Standard round: tabs 0–5 → round numbers 1–6, tabs 7–12 → round numbers 7–12
    const roundNumber =
      currentTabIndex < 6 ? currentTabIndex + 1 : currentTabIndex;
    const round = rounds.find((r) => r.round_number === roundNumber);
    if (!round) return null;

    return (
      <RoundEditor
        key={round.id}
        roundId={round.id}
        gameId={gameId}
        roundNumber={round.round_number}
        isNiche={round.round_number === 10}
        disabled={disabled}
        refreshTabState={refreshTabState}
      />
    );
  };

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <Box flex={1} overflow="hidden" display="flex" flexDirection="column">
      {renderTabs()}

      {incompleteErrors.length > 0 && (
        <Accordion sx={{ backgroundColor: theme.palette.primary.dark }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon color="error" />}>
            <Alert severity="error" sx={{ width: "100%", py: 0 }}>
              {incompleteErrors.length} incomplete{" "}
              {incompleteErrors.length === 1 ? "item" : "items"}
            </Alert>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              {incompleteErrors.map((e, i) => (
                <ListItem key={i} sx={{ color: "red" }}>
                  {e}
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {renderPageBody()}
    </Box>
  );
};

export default GameBoardEditor;
